import { Input, Select } from '@chakra-ui/react';
import { calcRecordByName, calcScoreByName } from './components/calcPoint';
import { Links } from './components/links';

import { TextBox } from './components/textbox';
import React, { useState, useEffect } from 'react';
import { HowToUse } from './components/howToUse';

const ageFactors = {
  M35: {
    i00: 0.9999,
    lj: 1.0385,
    sp: 1.0462,
    hj: 1.0136,
    A00: 0.9824,
    ii0h: 0.9957,
    dt: 1.0,
    pv: 1.0129,
    jt: 1.0438,
    i500: 0.9849,
  },
  M40: {
    i00: 0.9668,
    lj: 1.0972,
    sp: 1.1125,
    hj: 1.0631,
    A00: 0.9513,
    ii0h: 0.9609,
    dt: 1.0187,
    pv: 1.0708,
    jt: 1.1218,
    i500: 0.9532,
  },
  M45: {
    i00: 0.9345,
    lj: 1.1608,
    sp: 1.1867,
    hj: 1.1159,
    A00: 0.9208,
    ii0h: 0.9244,
    dt: 1.0856,
    pv: 1.1351,
    jt: 1.211,
    i500: 0.9206,
  },
  M50: {
    i00: 0.9031,
    lj: 1.2299,
    sp: 1.1551,
    hj: 1.1724,
    A00: 0.8909,
    ii0h: 0.9662,
    dt: 1.0078,
    pv: 1.207,
    jt: 1.2293,
    i500: 0.8871,
  },
  M55: {
    i00: 0.8726,
    lj: 1.3051,
    sp: 1.242,
    hj: 1.233,
    A00: 0.8616,
    ii0h: 0.923,
    dt: 1.0873,
    pv: 1.2881,
    jt: 1.3425,
    i500: 0.8527,
  },
  M60: {
    i00: 0.8429,
    lj: 1.3876,
    sp: 1.2252,
    hj: 1.2981,
    A00: 0.8329,
    ii0h: 0.9457,
    dt: 0.9653,
    pv: 1.38,
    jt: 1.3675,
    i500: 0.8174,
  },
  M65: {
    i00: 0.8139,
    lj: 1.4783,
    sp: 1.3317,
    hj: 1.3683,
    A00: 0.8047,
    ii0h: 0.8958,
    dt: 1.059,
    pv: 1.4854,
    jt: 1.5184,
    i500: 0.7814,
  },
  M70: {
    i00: 0.7858,
    lj: 1.5787,
    sp: 1.3036,
    hj: 1.4442,
    A00: 0.777,
    ii0h: 1.0788,
    dt: 1.1746,
    pv: 1.6073,
    jt: 1.5566,
    i500: 0.7446,
  },
  M75: {
    i00: 0.7584,
    lj: 1.6917,
    sp: 1.4385,
    hj: 1.5267,
    A00: 0.744,
    ii0h: 1.0111,
    dt: 1.3205,
    pv: 1.7502,
    jt: 1.7731,
    i500: 0.707,
  },
  M80: {
    i00: 0.7317,
    lj: 1.8448,
    sp: 1.3885,
    hj: 1.6166,
    A00: 0.697,
    ii0h: 0.9392,
    dt: 1.5103,
    pv: 1.9199,
    jt: 1.8402,
    i500: 0.6651,
  },
  M85: {
    i00: 0.6946,
    lj: 2.0674,
    sp: 1.5671,
    hj: 1.7149,
    A00: 0.636,
    ii0h: 0.8483,
    dt: 1.7672,
    pv: 2.1548,
    jt: 2.1894,
    i500: 0.6076,
  },
  M90: {
    i00: 0.6396,
    lj: 2.4042,
    sp: 1.7971,
    hj: 1.8493,
    A00: 0.5609,
    ii0h: 0.736,
    dt: 2.1341,
    pv: 2.5212,
    jt: 2.6989,
    i500: 0.5341,
  },
  M95: {
    i00: 0.5666,
    lj: 2.9522,
    sp: 2.1043,
    hj: 2.0563,
    A00: 0.4718,
    ii0h: 0.6025,
    dt: 2.7,
    pv: 3.1395,
    jt: 3.4861,
    i500: 0.4446,
  },
  M100: {
    i00: 0.4757,
    lj: 3.9676,
    sp: 2.5361,
    hj: 2.3825,
    A00: 0.3686,
    ii0h: 0.4477,
    dt: 3.6863,
    pv: 4.3531,
    jt: 4.7841,
    i500: 0.339,
  },
  M105: {
    i00: 0.3669,
    lj: 6.4179,
    sp: 3.1876,
    hj: 2.9328,
    A00: 0.2514,
    ii0h: 0.2719,
    dt: 5.8353,
    pv: 7.6795,
    jt: 7.2999,
    i500: 0.2175,
  },
  M110: {
    i00: 0.24,
    lj: 20.0,
    sp: 4.2841,
    hj: 4.0,
    A00: 0.12,
    ii0h: 0.075,
    dt: 14.1421,
    pv: 50.0,
    jt: 14.1421,
    i500: 0.08,
  },
} as const;

type EventData = {
  i00: string;
  lj: string;
  sp: string;
  hj: string;
  A00: string;
  ii0h: string;
  dt: string;
  pv: string;
  jt: string;
  i500: string;
};

const emptyEventData: EventData = {
  i00: '',
  lj: '',
  sp: '',
  hj: '',
  A00: '',
  ii0h: '',
  dt: '',
  pv: '',
  jt: '',
  i500: '',
};

type EventKey = keyof EventData;

const eventNameMastersDict = {
  i00: '100m',
  lj: '走幅跳',
  sp: '砲丸投',
  hj: '走高跳',
  A00: '400m',
  ii0h: 'ハードル',
  dt: '円盤投',
  pv: '棒高跳',
  jt: 'やり投',
  i500: '1500m',
};

const toSeconds = (time: number) => {
  const min = Math.floor(time / 100);
  const sec = time % 100;

  return min * 60 + sec;
};

const fromSeconds = (seconds: number) => {
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;

  return Math.ceil((min * 100 + sec) * 100) / 100;
};

const runningEvents: EventKey[] = ['i00', 'A00', 'ii0h', 'i500'];

const calcAdjustedRecord = (
  key: EventKey,
  record: number,
  category: keyof typeof ageFactors
) => {
  const factor = ageFactors[category][key];

  if (key === 'i500') {
    const adjustedSeconds = toSeconds(record) * factor;

    // 1500mも走種目なので1/100秒へ切り上げ
    return fromSeconds(adjustedSeconds);
  }

  const adjusted = record * factor;

  if (runningEvents.includes(key)) {
    // 100m・400m・ハードル
    return Math.ceil(adjusted * 100) / 100;
  }

  // 走幅跳・砲丸・走高跳・円盤・棒高跳・やり投
  return Math.floor(adjusted * 100) / 100;
};

const calcOriginalRecord = (
  key: EventKey,
  adjustedRecord: number,
  category: keyof typeof ageFactors
) => {
  const factor = ageFactors[category][key];

  if (key === 'i500') {
    return fromSeconds(toSeconds(adjustedRecord) / factor);
  }

  return Math.ceil((adjustedRecord / factor) * 100) / 100;
};

const createCalculatedData = (
  records: EventData,
  category: keyof typeof ageFactors
) => {
  const adjustedRecords = { ...emptyEventData };
  const originalPoints = { ...emptyEventData };
  const adjustedPoints = { ...emptyEventData };

  const eventList = Object.keys(emptyEventData) as EventKey[];

  eventList.forEach((key) => {
    const record = records[key];

    if (record === '') return;

    const adjusted = calcAdjustedRecord(key, Number(record) / 100, category);

    const adjustedDisplay = Math.round(adjusted * 100);

    adjustedRecords[key] = String(adjustedDisplay);

    originalPoints[key] = String(calcScoreByName(key, Number(record)));

    adjustedPoints[key] = String(calcScoreByName(key, adjustedDisplay));
  });

  return {
    adjustedRecords,
    originalPoints,
    adjustedPoints,
  };
};

const loadInitialData = () => {
  const defaultCategory: keyof typeof ageFactors = 'M40';
  const defaultRecords = { ...emptyEventData };

  try {
    const savedData = localStorage.getItem('MastersDecathlon_lastUsed');

    if (!savedData) {
      return {
        mastersCategory: defaultCategory,
        records: defaultRecords,
        ...createCalculatedData(defaultRecords, defaultCategory),
      };
    }

    const parsed = JSON.parse(savedData);

    const mastersCategory =
      (parsed.mastersCategory as keyof typeof ageFactors) ?? defaultCategory;

    const records = {
      ...emptyEventData,
      ...(parsed.records ?? {}),
    };

    return {
      mastersCategory,
      records,
      ...createCalculatedData(records, mastersCategory),
    };
  } catch (error) {
    console.error('保存データの読み込みに失敗しました', error);

    return {
      mastersCategory: defaultCategory,
      records: defaultRecords,
      ...createCalculatedData(defaultRecords, defaultCategory),
    };
  }
};

export const CalculatorMastersDecathlonSave = () => {
  const [initialData] = useState(() => loadInitialData());

  const [mastersCategory, setMastersCategory] = useState<
    keyof typeof ageFactors
  >(initialData.mastersCategory);

  const [records, setRecords] = useState<EventData>(initialData.records);

  const [adjustedRecords, setAdjustedRecords] = useState<EventData>(
    initialData.adjustedRecords
  );

  const [originalPoints, setOriginalPoints] = useState<EventData>(
    initialData.originalPoints
  );

  const [adjustedPoints, setAdjustedPoints] = useState<EventData>(
    initialData.adjustedPoints
  );

  const eventList = Object.keys(emptyEventData) as EventKey[];

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCategory = e.target.value as keyof typeof ageFactors;

    const calculated = createCalculatedData(records, newCategory);

    setMastersCategory(newCategory);
    setAdjustedRecords(calculated.adjustedRecords);
    setOriginalPoints(calculated.originalPoints);
    setAdjustedPoints(calculated.adjustedPoints);
  };

  const handleRecordChange =
    (key: EventKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
        .replace(/\D/g, '')
        .slice(0, getMaxLength(key));

      setRecords((prev) => ({
        ...prev,
        [key]: value,
      }));

      if (value === '') {
        setAdjustedRecords((prev) => ({ ...prev, [key]: '' }));
        setOriginalPoints((prev) => ({ ...prev, [key]: '' }));
        setAdjustedPoints((prev) => ({ ...prev, [key]: '' }));
        return;
      }

      const adjusted = calcAdjustedRecord(
        key,
        Number(value) / 100,
        mastersCategory as keyof typeof ageFactors
      );

      const adjustedDisplay = Math.round(adjusted * 100);

      setAdjustedRecords((prev) => ({
        ...prev,
        [key]: String(adjustedDisplay),
      }));

      setOriginalPoints((prev) => ({
        ...prev,
        [key]: String(calcScoreByName(key, Number(value))),
      }));

      setAdjustedPoints((prev) => ({
        ...prev,
        [key]: String(calcScoreByName(key, adjustedDisplay)),
      }));
    };

  const handleAdjustedRecordChange =
    (key: EventKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
        .replace(/\D/g, '')
        .slice(0, getMaxLength(key));

      setAdjustedRecords((prev) => ({ ...prev, [key]: value }));

      if (value === '') {
        setRecords((prev) => ({ ...prev, [key]: '' }));
        setOriginalPoints((prev) => ({ ...prev, [key]: '' }));
        setAdjustedPoints((prev) => ({ ...prev, [key]: '' }));
        return;
      }

      const original = calcOriginalRecord(
        key,
        Number(value) / 100,
        mastersCategory as keyof typeof ageFactors
      );

      const originalDisplay = Math.round(original * 100);

      setRecords((prev) => ({ ...prev, [key]: String(originalDisplay) }));
      setOriginalPoints((prev) => ({
        ...prev,
        [key]: String(calcScoreByName(key, originalDisplay)),
      }));
      setAdjustedPoints((prev) => ({
        ...prev,
        [key]: String(calcScoreByName(key, Number(value))),
      }));
    };

  const handleOriginalPointChange =
    (key: EventKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
        .replace(/\D/g, '')
        .slice(0, getMaxLength(key));

      setOriginalPoints((prev) => ({ ...prev, [key]: value }));

      if (value === '') {
        setRecords((prev) => ({ ...prev, [key]: '' }));
        setAdjustedRecords((prev) => ({ ...prev, [key]: '' }));
        setAdjustedPoints((prev) => ({ ...prev, [key]: '' }));
        return;
      }

      const originalRecord = calcRecordByName(key, Number(value));

      if (originalRecord === undefined) return;

      const originalDisplay = Math.round(originalRecord);

      setRecords((prev) => ({ ...prev, [key]: String(originalDisplay) }));

      const adjusted = calcAdjustedRecord(
        key,
        originalDisplay / 100,
        mastersCategory as keyof typeof ageFactors
      );

      const adjustedDisplay = Math.round(adjusted * 100);

      setAdjustedRecords((prev) => ({
        ...prev,
        [key]: String(adjustedDisplay),
      }));

      setAdjustedPoints((prev) => ({
        ...prev,
        [key]: String(calcScoreByName(key, adjustedDisplay)),
      }));
    };

  const handleAdjustedPointChange =
    (key: EventKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
        .replace(/\D/g, '')
        .slice(0, getMaxLength(key));

      setAdjustedPoints((prev) => ({ ...prev, [key]: value }));

      if (value === '') {
        setRecords((prev) => ({ ...prev, [key]: '' }));
        setAdjustedRecords((prev) => ({ ...prev, [key]: '' }));
        setOriginalPoints((prev) => ({ ...prev, [key]: '' }));
        return;
      }

      const adjustedRecord = calcRecordByName(key, Number(value));

      if (adjustedRecord === undefined) return;

      const adjustedDisplay = Math.round(adjustedRecord);

      setAdjustedRecords((prev) => ({
        ...prev,
        [key]: String(adjustedDisplay),
      }));

      const original = calcOriginalRecord(
        key,
        adjustedDisplay / 100,
        mastersCategory as keyof typeof ageFactors
      );

      const originalDisplay = Math.round(original * 100);

      setRecords((prev) => ({ ...prev, [key]: String(originalDisplay) }));
      setOriginalPoints((prev) => ({
        ...prev,
        [key]: String(calcScoreByName(key, originalDisplay)),
      }));
    };

  const calcRunningTotal = (targetKey: EventKey, pointData: EventData) => {
    let total = 0;

    for (const key of eventList) {
      total += Number(pointData[key]) || 0;

      if (key === targetKey) {
        break;
      }
    }

    return total;
  };

  const totalOriginalPoint = Object.values(originalPoints).reduce(
    (sum, point) => sum + (Number(point) || 0),
    0
  );

  const totalAdjustedPoint = Object.values(adjustedPoints).reduce(
    (sum, point) => sum + (Number(point) || 0),
    0
  );

  const getMaxLength = (key: EventKey) => {
    switch (key) {
      case 'i500':
        return 5;

      default:
        return 4;
    }
  };

  useEffect(() => {
    localStorage.setItem(
      'MastersDecathlon_lastUsed',
      JSON.stringify({
        mastersCategory,
        records,
      })
    );
  }, [mastersCategory, records]);

  return (
    <div
      style={{
        width: '500px',
        minWidth: '500px',
        overflowX: 'auto',
      }}
    >
      {/* タイトル部分の実装 */}
      <div
        style={{ marginLeft: '70px', marginTop: '16px', marginBottom: '16px' }}
      >
        マスターズ十種競技得点表
      </div>
      <div style={{ marginLeft: '16px', marginBottom: '10px' }}>
        カテゴリーを選んでください
        <Select
          width="100px"
          value={mastersCategory}
          onChange={handleCategoryChange}
        >
          {Object.keys(ageFactors).map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </Select>
      </div>
      　入力は小数点を省略して入力してください。
      <br />
      　　12秒00→1200
      <br />
      　　6m00→600
      <br />
      　　5分00秒00→50000
      {eventList.map((key) => (
        <div key={key} style={{ marginLeft: '16px', marginTop: '10px' }}>
          <div>　　　　　{eventNameMastersDict[key]}</div>
          <span style={{ width: '70px', display: 'inline-block' }}>実記録</span>
          <Input
            value={records[key]}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            style={{
              width: '90px',
              textAlign: 'left',
            }}
            onChange={handleRecordChange(key)}
            placeholder="記録"
          />
          　
          <Input
            value={originalPoints[key]}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            style={{
              width: '80px',
              textAlign: 'right',
            }}
            onChange={handleOriginalPointChange(key)}
            placeholder="得点"
          />
          点 　合計:
          {calcRunningTotal(key, originalPoints)}
          <div></div>
          <span style={{ width: '70px', display: 'inline-block' }}>
            {' '}
            {mastersCategory}換算
          </span>
          <Input
            value={adjustedRecords[key]}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            style={{
              width: '90px',
              textAlign: 'left',
            }}
            onChange={handleAdjustedRecordChange(key)}
            placeholder="記録"
          />
          　
          <Input
            value={adjustedPoints[key]}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            style={{
              width: '80px',
              textAlign: 'right',
            }}
            onChange={handleAdjustedPointChange(key)}
            placeholder="得点"
          />
          点 　合計:
          {calcRunningTotal(key, adjustedPoints)}
        </div>
      ))}
      <div style={{ marginLeft: '16px', marginTop: '16px' }}>
        実記録得点合計：{totalOriginalPoint} 点
      </div>
      <div style={{ marginLeft: '16px', marginTop: '4px' }}>
        {mastersCategory}換算得点合計：{totalAdjustedPoint} 点
      </div>
      <TextBox />
      <Links />
    </div>
  );
};
